/* ============================================================
   usePersistence — IndexedDB tab/comment store

   Ported from prototype/reader.html section 2 + the
   cleanTabForSave / saveTabSoon / saveTabImmediate logic. Object
   store 'tabs' keyed by `hash`; debounced (≈400 ms) saves for
   high-frequency edits; transient/draft state stripped before
   every write.
   ============================================================ */

import type { Tab, Comment } from '@/types'

const DB_NAME = 'md-reader'
const STORE_NAME = 'tabs'
const DB_VERSION = 1

let dbInstance: IDBDatabase | null = null
let openPromise: Promise<IDBDatabase> | null = null
// IndexedDB can be absent (private mode, some file:// contexts — e.g. a
// downloaded portable snapshot). Persistence then degrades to a no-op so the
// reader still renders; it just won't remember tabs.
let dbUnavailable = false

// indexedDB.open() can hang indefinitely — if the DB is blocked by a pending
// deleteDatabase / version change held open by another tab, neither onsuccess
// nor onerror ever fires. ready() awaits open(), so a hang would stall the
// whole reader, including the shared-plan view that is the public entry point.
// Cap the wait and degrade to a no-op store rather than block.
const OPEN_TIMEOUT_MS = 3000

function open(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance)
  if (openPromise) return openPromise
  openPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'))
      return
    }
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      reject(new Error('IndexedDB open timed out (blocked?)'))
    }, OPEN_TIMEOUT_MS)
    const finish = (run: () => void): void => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      run()
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const idb = req.result
      if (!idb.objectStoreNames.contains(STORE_NAME)) {
        idb.createObjectStore(STORE_NAME, { keyPath: 'hash' })
      }
    }
    req.onsuccess = () =>
      finish(() => {
        dbInstance = req.result
        resolve(dbInstance)
      })
    req.onerror = () => finish(() => reject(req.error))
    // Fires when our open is held off by another connection blocking a version
    // change — don't wait it out, degrade now.
    req.onblocked = () => finish(() => reject(new Error('IndexedDB open blocked')))
  })
  return openPromise
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (idb) =>
      new Promise<T>((resolve, reject) => {
        const transaction = idb.transaction(STORE_NAME, mode)
        const store = transaction.objectStore(STORE_NAME)
        const req = fn(store)
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      }),
  )
}

/** Run a read transaction, returning `fallback` (and disabling persistence) if the DB is unavailable. */
async function safeRead<T>(
  fn: (store: IDBObjectStore) => IDBRequest<T>,
  fallback: T,
): Promise<T> {
  if (dbUnavailable) return fallback
  try {
    return await tx('readonly', fn)
  } catch {
    dbUnavailable = true
    return fallback
  }
}

/** Run a write transaction, silently no-op'ing (and disabling persistence) if the DB is unavailable. */
async function safeWrite<T>(fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<void> {
  if (dbUnavailable) return
  try {
    await tx('readwrite', fn)
  } catch {
    dbUnavailable = true
  }
}

const db = {
  ready: async () => {
    try {
      await open()
    } catch {
      dbUnavailable = true
    }
  },
  get: (hash: string) => safeRead<Tab | undefined>((s) => s.get(hash), undefined),
  put: (tab: Tab) => safeWrite((s) => s.put(tab)),
  delete: (hash: string) => safeWrite((s) => s.delete(hash)),
  listAll: () =>
    safeRead<Tab[]>((s) => s.getAll(), []).then((rows) =>
      rows.sort((a, b) => b.updatedAt - a.updatedAt),
    ),
  clearAll: () => safeWrite((s) => s.clear()),
}

// ---- debounce (ported verbatim; supports .flush) ----
interface Debounced<A extends unknown[]> {
  (...args: A): void
  flush: () => void
}
function debounce<A extends unknown[]>(fn: (...args: A) => void, wait: number): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null
  let pendingArgs: A | null = null
  const wrapped = ((...args: A) => {
    pendingArgs = args
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      const a = pendingArgs
      pendingArgs = null
      if (a) fn(...a)
    }, wait)
  }) as Debounced<A>
  wrapped.flush = () => {
    if (timer === null) return
    clearTimeout(timer)
    timer = null
    const a = pendingArgs
    pendingArgs = null
    if (a) fn(...a)
  }
  return wrapped
}

/**
 * Strip transient state (draft comments, transient edit fields, activeCommentId)
 * before persisting. Mirrors the prototype's cleanTabForSave.
 *
 * Returns a PLAIN deep copy. The live tab is Vue-reactive, so nested objects
 * read through it (a comment's `author`, `replies`, `resolved`) are Proxies,
 * which IndexedDB's structured clone rejects with a DataCloneError — and one
 * failed write disables persistence for the session. The JSON round-trip
 * reads through the proxies and yields plain data (a Tab is JSON by design).
 */
export function cleanTabForSave(tab: Tab): Tab {
  const cleaned: Tab = {
    ...tab,
    comments: tab.comments
      .filter((c) => !c._draft)
      .map((c) => {
        const cc: Comment = { ...c }
        delete cc._draft
        delete cc._editing
        delete cc._editPrev
        delete cc._collapsed
        delete cc._anchorLost
        delete cc._replying
        delete cc._replyDraft
        return cc
      }),
    activeCommentId: null,
  }
  return JSON.parse(JSON.stringify(cleaned)) as Tab
}

const tabSavers = new Map<string, Debounced<[]>>()

/** Hooks invoked after a save lands, wired by the Reader (recent list, doc meta). */
let onSaved: ((tab: Tab) => void) | null = null
export function setOnSaved(fn: (tab: Tab) => void): void {
  onSaved = fn
}

export function usePersistence() {
  async function ready(): Promise<void> {
    return db.ready()
  }
  async function getTab(hash: string): Promise<Tab | undefined> {
    return db.get(hash)
  }
  async function putTab(tab: Tab): Promise<void> {
    return db.put(cleanTabForSave(tab))
  }
  async function deleteTab(hash: string): Promise<void> {
    return db.delete(hash)
  }
  async function listAll(): Promise<Tab[]> {
    return db.listAll()
  }
  async function clearAll(): Promise<void> {
    return db.clearAll()
  }

  /** Debounced save (≈400 ms) for high-frequency edits. */
  function saveTabSoon(tab: Tab): void {
    let s = tabSavers.get(tab.hash)
    if (!s) {
      s = debounce(() => {
        db.put(cleanTabForSave(tab))
        onSaved?.(tab)
      }, 400)
      tabSavers.set(tab.hash, s)
    }
    s()
  }

  /** Flush pending debounce + persist immediately. */
  async function saveTabImmediate(tab: Tab): Promise<void> {
    const s = tabSavers.get(tab.hash)
    if (s) s.flush()
    await db.put(cleanTabForSave(tab))
    onSaved?.(tab)
  }

  return {
    ready,
    getTab,
    putTab,
    deleteTab,
    listAll,
    clearAll,
    saveTabSoon,
    saveTabImmediate,
  }
}
